// src/pages/account/AdminUsers.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { dynastyTheme } from '../../services/colorService';
import apiService from '../../services/apiService';
import { formatApiError } from '../../services/errorUtils';
import { ArrowDown, ArrowUp, Info, Mail, RefreshCw, Users } from 'lucide-react';
import MessagingPortal from './MessagingPortal';

/**
 * DD-ADMIN-PAGE-0728 — platform-owner user roster.
 *
 * Answers one question: who is actually arriving. Every signup, sortable by signup and
 * last-active, with the project's own test identities flagged so the real count is legible at a
 * glance (the investigation that prompted this found 50 accounts of which only 11 were real
 * people, and exactly one an organic outside arrival).
 *
 * THIS TAB IS COSMETIC SECURITY ONLY. It is hidden from non-owners in MyAccount.js purely so
 * nobody clicks a tab that will 403. The real gate is server-side —
 * `core.authz.verify_platform_owner` on GET /api/account/admin/users, plus an independent
 * re-check in the service — because anything shipped to a browser is readable by anyone.
 *
 * LAST-ACTIVE IS NOT INVENTED HERE. The backend reports it null with a reason; this renders
 * "Not tracked" and shows the reason rather than substituting a field that merely looks like
 * activity (profile updated_at, a league join date). See admin_user_service.LAST_ACTIVE_NOTE.
 */

const SORTABLE = {
    signed_up_at: 'Signed up',
    last_active_at: 'Last active',
};

const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * DD-LAST-ACTIVE-0728 — "3h ago" beats a date for the question this column answers ("is this
 * person still around?"), but the exact timestamp stays in the title attribute so nothing is
 * hidden. Values are naive UTC from PostgreSQL; the Z suffix is added when absent so the browser
 * does not silently read them as local time and shift every row by the timezone offset.
 */
const asUTC = (iso) => new Date(/[Z+]|-\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);

const formatRelative = (iso) => {
    if (!iso) return null;
    const d = asUTC(iso);
    if (Number.isNaN(d.getTime())) return null;

    const seconds = Math.round((Date.now() - d.getTime()) / 1000);
    if (seconds < 0) return 'just now';          // small clock skew, not the future
    if (seconds < 90) return 'just now';
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatExact = (iso) => (iso ? asUTC(iso).toLocaleString() : undefined);

const AdminUsers = ({ setMessage }) => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // DD-LAST-ACTIVE-FIX-0728: default to LAST-ACTIVE, not signup. Sorted by signup, every user
    // with a recorded visit fell to the bottom (rows 37/45/50 of 50 — the owner last), so the
    // page opened on ~36 consecutive "Not tracked" rows and read as a broken feature. The column
    // that answers "who is actually around" should be the one the table opens on.
    const [sortKey, setSortKey] = useState('last_active_at');
    const [sortDir, setSortDir] = useState('desc');
    // DD-ADMIN-MESSAGING-0728: the messaging portal lives inside this owner-only view rather than
    // as its own tab — it targets the very people this table is here to surface.
    const [view, setView] = useState('roster');

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiService.get('/api/account/admin/users');
            setData(response.data);
        } catch (err) {
            const message = formatApiError(err, 'Failed to load the user roster');
            setError(message);
            if (setMessage) setMessage({ text: message, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [setMessage]);

    useEffect(() => { load(); }, [load]);

    const toggleSort = (key) => {
        if (key === sortKey) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    const rows = useMemo(() => {
        const users = data?.users ? [...data.users] : [];
        // Compared as PARSED INSTANTS, not strings: the two sortable columns arrive in different
        // shapes — signed_up_at is Cognito's offset-bearing ISO ("…T23:25:46+00:00"), while
        // last_active_at is a naive PostgreSQL timestamp ("… 13:04:19.955"). Lexicographic
        // ordering happens to work within each column today and would break the day either
        // format shifts.
        //
        // Nulls always sort LAST, in BOTH directions. An untracked value is absent, not
        // "earliest" — sorting never-seen users to the top of "most recently active" would read
        // as a claim about them.
        const key = (u) => {
            const raw = u[sortKey];
            if (!raw) return null;
            const t = asUTC(raw).getTime();
            return Number.isNaN(t) ? null : t;
        };
        users.sort((a, b) => {
            const av = key(a);
            const bv = key(b);
            if (av === null && bv === null) return 0;
            if (av === null) return 1;
            if (bv === null) return -1;
            return sortDir === 'asc' ? av - bv : bv - av;
        });
        return users;
    }, [data, sortKey, sortDir]);

    const SortHeader = ({ column, label }) => (
        <th className="text-left px-4 py-3">
            <button
                onClick={() => toggleSort(column)}
                className="flex items-center gap-1 font-semibold hover:text-yellow-300 transition-colors"
            >
                {label}
                {sortKey === column && (
                    sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                )}
            </button>
        </th>
    );

    if (isLoading) {
        return (
            <div className={`${dynastyTheme.components.card.base} p-8 flex items-center gap-3`}>
                <RefreshCw size={20} className="animate-spin text-yellow-400" />
                <span className={dynastyTheme.classes.text.neutralLight}>Loading users…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`${dynastyTheme.components.card.base} p-8`}>
                <p className="text-red-400 mb-4">{error}</p>
                <button onClick={load} className={dynastyTheme.utils.getComponent('button', 'primary', 'sm')}>
                    Try again
                </button>
            </div>
        );
    }

    return (
        <div className={`${dynastyTheme.components.card.base} p-6`}>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <Users size={22} className="text-yellow-400" />
                    <div>
                        <h3 className={dynastyTheme.components.heading.h3}>Users</h3>
                        <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                            <span className="text-yellow-400 font-semibold">{data?.real_count ?? 0} real</span>
                            {' · '}
                            {data?.total_count ?? 0} total
                            {data?.synthetic_count ? ` (${data.synthetic_count} test accounts)` : ''}
                            {' · '}
                            <span className="text-emerald-400">
                                {data?.seen_count ?? 0} seen since tracking began
                            </span>
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setView(view === 'roster' ? 'messaging' : 'roster')}
                        className={`${dynastyTheme.utils.getComponent('button', 'primary', 'sm')} flex items-center gap-2`}
                    >
                        <Mail size={16} /> {view === 'roster' ? 'Message users' : 'Back to list'}
                    </button>
                    <button
                        onClick={load}
                        className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'sm')} flex items-center gap-2`}
                    >
                        <RefreshCw size={16} /> Refresh
                    </button>
                </div>
            </div>

            {view === 'messaging' && <MessagingPortal setMessage={setMessage} />}

            {view === 'roster' && data?.last_active_note && (
                <div className="flex items-start gap-2 mb-6 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                    <Info size={16} className="text-blue-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-blue-200">
                        <span className="font-semibold">Last active: </span>{data.last_active_note}
                    </p>
                </div>
            )}

            {view === 'roster' && (
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className={`${dynastyTheme.classes.text.neutralLight} border-b border-gray-700`}>
                        <tr>
                            <th className="text-left px-4 py-3 font-semibold">Name</th>
                            <th className="text-left px-4 py-3 font-semibold">Email</th>
                            <SortHeader column="signed_up_at" label={SORTABLE.signed_up_at} />
                            <SortHeader column="last_active_at" label={SORTABLE.last_active_at} />
                            <th className="text-left px-4 py-3 font-semibold">Leagues</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((u) => (
                            <tr
                                key={u.user_id}
                                className={`border-b border-gray-800 ${u.is_synthetic ? 'opacity-50' : ''}`}
                            >
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-white">{u.name || '—'}</span>
                                        {u.is_synthetic && (
                                            <span className="px-2 py-0.5 text-xs rounded bg-gray-700 text-gray-300">
                                                test
                                            </span>
                                        )}
                                        {u.account_status === 'UNCONFIRMED' && (
                                            <span className="px-2 py-0.5 text-xs rounded bg-amber-500/20 text-amber-300">
                                                unconfirmed
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className={`px-4 py-3 ${dynastyTheme.classes.text.neutralLight}`}>
                                    {u.email || '—'}
                                </td>
                                <td className={`px-4 py-3 ${dynastyTheme.classes.text.neutralLight}`}>
                                    {formatDate(u.signed_up_at) || '—'}
                                </td>
                                <td className={`px-4 py-3 ${dynastyTheme.classes.text.neutralLight}`}>
                                    {/* Real time when we have one; "Not tracked" is an absence of
                                        data, NOT a claim of inactivity — the banner says which. */}
                                    {u.last_active_at ? (
                                        <span title={formatExact(u.last_active_at)}>
                                            {formatRelative(u.last_active_at)}
                                        </span>
                                    ) : (
                                        <span className="text-gray-500 italic">Not tracked</span>
                                    )}
                                </td>
                                <td className={`px-4 py-3 ${dynastyTheme.classes.text.neutralLight}`}>
                                    {u.leagues?.length
                                        ? u.leagues.map((l, i) => (
                                            <div key={`${u.user_id}-${i}`}>
                                                {l.league_name}
                                                {l.role ? <span className="text-gray-500"> · {l.role}</span> : null}
                                            </div>
                                        ))
                                        : '—'}
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                    No users found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            )}
        </div>
    );
};

export default AdminUsers;
